import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { api } from "../axios/api";
import { numberFormat } from "../config/Config";
import {
  DollarSign, TrendingUp, FileText, ShoppingCart, 
  Percent, Tag, Archive, AlertTriangle, Package
} from "lucide-react";
import Loader from "../components/Loader";

const Dashboard = () => {
  const user = useSelector((state) => state.user.user);
  const empresaId = user?.empresa_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!empresaId) return;
      try {
        setLoading(true);
        const res = await api.get(`/app/dashboard/${empresaId}`);
        setData(res.data);
      } catch (err) {
        console.error("Erro ao buscar dados do painel:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [empresaId]);

  if (loading || !data) return <Loader />;

  const { resumo, graficos, recentes } = data;

  const salesChartOptions = {
    chart: { toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
    plotOptions: { bar: { borderRadius: 4, columnWidth: '30%' } },
    xaxis: { categories: graficos.vendasPorMes?.map(v => v.mes) || [] },
    colors: ["#2563eb"],
    grid: { borderColor: "#f1f5f9", strokeDashArray: 4 }
  };

  const docMixChartOptions = {
    labels: graficos.vendasPorTipo?.map(v => v.doc_type) || [],
    legend: { position: 'bottom' },
    colors: ["#3b82f6", "#10b981", "#f59e0b", "#7c3aed", "#dc2626"],
    plotOptions: { pie: { donut: { size: '70%' } } }
  };

  return (
    <div id="layout-wrapper" className="dashboard-financeiro">
      <Navbar />
      <Sidebar />

      <style>{`
        .dashboard-financeiro { background-color: #f8fafc; min-height: 100vh; }
        .app-wrapper { padding: 24px; }
        .metric-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; transition: transform 0.2s; }
        .card-title-flat { font-size: 11px; font-weight: 800; color: #64748b; text-transform: uppercase; }
        .table-clean th { font-size: 11px; color: #64748b; text-transform: uppercase; padding: 12px; background: #f8fafc; }
        .table-clean td { padding: 14px 12px; font-size: 13px; border-bottom: 1px solid #f1f5f9; }
      `}</style>

      <main className="app-wrapper">
        <div className="container-fluid">
          <h2 className="fw-bold mb-4">Dashboard Financeiro</h2>

          {/* Foco: Hoje */}
          <div className="row g-3 mb-4">
            {[
              { label: "Vendas Hoje", valor: resumo.totalVendido, icon: <DollarSign size={20}/>, color: "#2563eb" },
              { label: "Recebido Hoje", valor: resumo.totalRecebido, icon: <TrendingUp size={20}/>, color: "#10b981" },
              { label: "IVA (Hoje)", valor: resumo.totalIVA, icon: <Percent size={20}/>, color: "#6366f1" },
              { label: "Descontos (Hoje)", valor: resumo.totalDescontos, icon: <Tag size={20}/>, color: "#f59e0b" },
            ].map((item, i) => (
              <div key={i} className="col-12 col-md-3">
                <div className="metric-card border-start border-4" style={{ borderLeftColor: item.color }}>
                  <div className="d-flex justify-content-between text-muted mb-2">
                    <span className="card-title-flat">{item.label}</span>
                    <div style={{ color: item.color }}>{item.icon}</div>
                  </div>
                  <h4 className="fw-bold mb-0">{numberFormat(item.valor, 2, ",", ".")} Kz</h4>
                </div>
              </div>
            ))}
          </div>

          {/* Operacional */}
          <div className="row g-3 mb-4">
            {[
              { label: "Custo Mercadorias", valor: resumo.custoMercadorias, icon: <ShoppingCart size={20}/> },
              { label: "Lucro Bruto", valor: resumo.lucroBruto || resumo.lucroEstimado, icon: <TrendingUp size={20}/> },
              { label: "Margem Lucro", valor: resumo.margemLucro, icon: <Percent size={20}/>, suffix: "%" },
              { label: "Produtos s/ Stock", valor: resumo.produtosSemStock, icon: <Archive size={20}/> },
              { label: "Stock Baixo", valor: resumo.produtosBaixoStock, icon: <AlertTriangle size={20}/> },
              { label: "Valor Inventário", valor: resumo.valorInventario, icon: <Package size={20}/> },
            ].map((item, i) => (
              <div key={i} className="col-12 col-md-3">
                <div className="metric-card">
                  <div className="d-flex justify-content-between text-muted mb-2">
                    <span className="card-title-flat">{item.label}</span>
                    {item.icon}
                  </div>
                  <h5 className="fw-bold mb-0">{item.suffix ? `${numberFormat(item.valor || 0, 2, ",", ".")}${item.suffix}` : item.label.includes("Stock") ? item.valor : numberFormat(item.valor || 0, 2, ",", ".") + " Kz"}</h5>
                </div>
              </div>
            ))}
          </div>

          {/* Gráficos */}
          <div className="row g-4 mb-4">
            <div className="col-xl-8">
              <div className="metric-card">
                <span className="card-title-flat">Vendas Mensais</span>
                <ReactApexChart type="bar" height={300} series={[{ name: "Vendas", data: graficos.vendasPorMes?.map(v => v.total_vendas) || [] }]} options={salesChartOptions} />
              </div>
            </div>
            <div className="col-xl-4">
              <div className="metric-card">
                <span className="card-title-flat">Mix de Documentos</span>
                <ReactApexChart type="donut" height={260} series={graficos.vendasPorTipo?.map(v => parseFloat(v.total)) || []} options={docMixChartOptions} />
              </div>
            </div>
          </div>

          {/* Recentes */}
          <div className="metric-card">
            <span className="card-title-flat">Últimos Documentos Emitidos</span>
            <table className="table-clean w-100 mt-3">
              <thead><tr><th>Nº</th><th>Cliente</th><th>Total</th><th>Estado</th></tr></thead>
              <tbody>
                {recentes.ultimosDocumentos?.map((doc, i) => (
                  <tr key={i}>
                    <td className="fw-bold">{doc.numero}</td>
                    <td>{doc.Cliente?.nome}</td>
                    <td>{numberFormat(doc.total_geral, 2, ",", ".")} Kz</td>
                    <td><span className="badge bg-success-subtle text-success">{doc.estado}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="metric-card mt-4">
            <span className="card-title-flat">Lucro por Cliente</span>
            <table className="table-clean w-100 mt-3">
              <thead><tr><th>Cliente</th><th>Receita</th><th>Custo</th><th>Lucro</th><th>Margem</th></tr></thead>
              <tbody>
                {graficos.lucroPorCliente?.map((row, i) => (
                  <tr key={i}>
                    <td className="fw-bold">{row.Cliente?.nome}</td>
                    <td>{numberFormat(row.receita, 2, ",", ".")} Kz</td>
                    <td>{numberFormat(row.custo, 2, ",", ".")} Kz</td>
                    <td className="fw-bold text-success">{numberFormat(row.lucro, 2, ",", ".")} Kz</td>
                    <td>{numberFormat(row.margem, 2, ",", ".")}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default Dashboard;
